import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { isAuthenticated, getAuthHeaders } from '../utils/auth';
import { API_ENDPOINTS } from '../config/api';
import './SessionTranscript.css';

/**
 * Parse raw SSE / OpenAI streaming data into readable text.
 * The backend sometimes stores the raw "data: {json}" lines instead of the
 * extracted content.  This function detects that and reassembles the actual
 * answer text so the transcript is human-readable.
 */
function parseSSEText(raw) {
  if (typeof raw !== 'string') return raw;

  // Quick check: if the text does NOT look like raw SSE data, return as-is
  if (!raw.includes('data:') || !raw.includes('chat.completion.chunk')) {
    return raw;
  }

  let assembled = '';
  const lines = raw.split('\n');

  for (let line of lines) {
    line = line.trim();
    if (!line.startsWith('data:')) continue;

    // Strip one or more "data:" prefixes
    let data = line.replace(/^(data:\s*)+/, '').trim();

    if (!data || data === '[DONE]') continue;

    try {
      const parsed = JSON.parse(data);

      // Standard OpenAI chat.completion.chunk → choices[0].delta.content
      if (parsed.choices && Array.isArray(parsed.choices)) {
        for (const choice of parsed.choices) {
          if (choice.delta && choice.delta.content) {
            assembled += choice.delta.content;
          } else if (choice.message && choice.message.content) {
            assembled += choice.message.content;
          }
        }
        continue;
      }

      // OpenAI Realtime / Responses API format
      if (parsed.type === 'response.output_text.delta' || parsed.type === 'response.content_part.delta') {
        assembled += parsed.delta || parsed.text || parsed.content || '';
        continue;
      }

      // Direct text / content / delta fields
      if (parsed.text) { assembled += parsed.text; continue; }
      if (parsed.content) { assembled += parsed.content; continue; }
      if (parsed.delta && typeof parsed.delta === 'string') { assembled += parsed.delta; continue; }
      if (parsed.delta && typeof parsed.delta === 'object') {
        assembled += parsed.delta.text || parsed.delta.content || '';
      }
    } catch {
      // Not valid JSON – skip
    }
  }

  return assembled.trim() || raw; // Fall back to original if parsing yields nothing
}

export default function SessionTranscript() {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const company = searchParams.get('company') || 'Interview';

  const [transcript, setTranscript] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Auth guard
  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/signup');
    }
  }, [navigate]);

  // Scroll-to-top visibility
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch transcript
  useEffect(() => {
    if (!sessionId) return;

    const fetchTranscript = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(API_ENDPOINTS.SESSIONS.TRANSCRIPT(sessionId), {
          method: 'GET',
          credentials: 'include',
          headers: getAuthHeaders(),
        });

        if (response.status === 404) {
          // No transcript available
          setTranscript({ entries: [] });
          setLoading(false);
          return;
        }

        if (!response.ok) {
          throw new Error(`Failed to load transcript (${response.status})`);
        }

        const data = await response.json();
        setTranscript(data);
      } catch (err) {
        console.error('Error fetching transcript:', err);
        setError(err.message || 'Failed to load transcript');
      }
      setLoading(false);
    };

    fetchTranscript();
  }, [sessionId]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="session-transcript-page">
      <div className="transcript-header">
        <div className="title-section">
          <span className="icon-bubble">📝</span>
          <h1>Session Transcript</h1>
        </div>
        <button className="transcript-back-btn" onClick={() => navigate('/sessions')}>
          ← Back to Sessions
        </button>
      </div>

      {/* Metadata bar */}
      {transcript && !loading && !error && transcript.entries && transcript.entries.length > 0 && (
        <div className="transcript-meta">
          <div className="transcript-meta-item">
            🏢 <span className="meta-value">{company}</span>
          </div>
          <div className="transcript-meta-item">
            💬 Questions: <span className="meta-value">{transcript.totalQuestions ?? transcript.entries.filter(e => e.type === 'QUESTION').length}</span>
          </div>
          <div className="transcript-meta-item">
            🤖 Answers: <span className="meta-value">{transcript.totalAnswers ?? transcript.entries.filter(e => e.type === 'AI_ANSWER').length}</span>
          </div>
          {transcript.firstTimestamp && (
            <div className="transcript-meta-item">
              🕐 Start: <span className="meta-value">{transcript.firstTimestamp}</span>
            </div>
          )}
          {transcript.lastTimestamp && (
            <div className="transcript-meta-item">
              🕐 End: <span className="meta-value">{transcript.lastTimestamp}</span>
            </div>
          )}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="transcript-loading">
          <div className="spinner"></div>
          <span>Loading transcript...</span>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="transcript-error">
          <div className="error-icon">⚠️</div>
          <h3>Unable to load transcript</h3>
          <p>{error}</p>
          <button className="transcript-back-btn" onClick={() => navigate('/sessions')}>
            ← Back to Sessions
          </button>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && transcript && transcript.entries && transcript.entries.length === 0 && (
        <div className="transcript-empty">
          <div className="empty-icon">📭</div>
          <p>No transcript available for this session.</p>
          <p style={{ fontSize: '13px', color: '#4b5563' }}>
            Transcripts are generated automatically during interview sessions with AI assistance enabled.
          </p>
        </div>
      )}

      {/* Transcript timeline */}
      {!loading && !error && transcript && transcript.entries && transcript.entries.length > 0 && (
        <div className="transcript-timeline">
          {transcript.entries.map((entry, index) => {
            const isQuestion = entry.type === 'QUESTION';
            const typeClass = isQuestion ? 'question' : 'answer';
            const icon = isQuestion ? '❓' : '💡';

            return (
              <div key={index} className="transcript-entry">
                <div className={`transcript-dot ${typeClass}`}>
                  {icon}
                </div>
                <div className={`transcript-card ${typeClass}`}>
                  <div className="transcript-card-header">
                    <span className={`transcript-card-label ${typeClass}`}>
                      {isQuestion ? 'Panel Question' : 'AI Answer'}
                    </span>
                    {entry.timestamp && (
                      <span className="transcript-card-time">{entry.timestamp}</span>
                    )}
                  </div>
                  <div className="transcript-card-text">
                    {parseSSEText(entry.text)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Scroll to top */}
      {showScrollTop && (
        <button className="transcript-scroll-top" onClick={scrollToTop} title="Scroll to top">
          ↑
        </button>
      )}
    </div>
  );
}
